import dayjs from 'dayjs';

/**
 * Export Product Status Change report to PDF
 * Matches the structure from ProductStatusChangeReport component
 * Based on the MERCK PRODUCT STATUS CHANGE CONFIRMATION form design
 */
export const exportProductStatusChangeToPdf = async (productStatusChange, includeAttachments = false) => {
  try {
    // Dynamic imports to avoid circular dependencies
    const pdfMake = await import('pdfmake/build/pdfmake');
    const pdfFonts = await import('pdfmake/build/vfs_fonts');
    const { API_BASE_URL } = await import('../../../config/apiConfig');

    // Resolve vfs: CRA/Webpack may expose it as default, default.vfs, or vfs
    const vfs = pdfFonts.vfs || pdfFonts.default?.vfs || pdfFonts.default || {};
    if (Object.keys(vfs).length === 0) {
      console.warn('PDF vfs_fonts empty; fonts may not render.');
    }

    // Define logo image key for pdfMake
    const logoImageKey = 'companyLogo';

    // Fetch company logo from application settings
    let companyLogoBase64 = null;
    try {
      const token = localStorage.getItem('authToken');
      const logoSettingResponse = await fetch(`${API_BASE_URL}/application-setting/key/companyLogo`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (logoSettingResponse.ok) {
        const logoSetting = await logoSettingResponse.json();
        if (logoSetting && logoSetting.value && logoSetting.isActive !== false) {
          let logoUrl = logoSetting.value.trim();
          
          // Construct full URL if not already absolute
          if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
            const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
            if (logoUrl.startsWith('/')) {
              logoUrl = `${baseUrl}${logoUrl}`;
            } else {
              logoUrl = `${baseUrl}/${logoUrl}`;
            }
          }

          // Convert image to base64
          try {
            const imageResponse = await fetch(logoUrl);
            if (imageResponse.ok) {
              const blob = await imageResponse.blob();
              const reader = new FileReader();
              companyLogoBase64 = await new Promise((resolve) => {
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
            }
          } catch (imgError) {
            console.warn('Could not load company logo image:', imgError);
          }
        }
      }
    } catch (error) {
      console.warn('Could not load company logo:', error);
    }

    // Helper functions
    const formatDate = (date) => {
      if (!date) return '';
      try {
        return dayjs(date).format('DD/MM/YYYY');
      } catch {
        return '';
      }
    };

    const formatDateTime = (date) => {
      if (!date) return '';
      try {
        return dayjs(date).format('DD/MM/YYYY HH:mm');
      } catch {
        return '';
      }
    };

    // Get status checkbox states
    const statusName = productStatusChange?.productStatus?.name || '';
    const isQuarantined = statusName.toLowerCase().includes('quarantine') || statusName.toLowerCase() === 'quarantined';
    const isRejected = statusName.toLowerCase().includes('reject') || statusName.toLowerCase() === 'rejected';
    const isReleased = statusName.toLowerCase().includes('release') || statusName.toLowerCase() === 'released';

    // Compute last approval request (if any), similar to complaint PDF
    const approvalRequests = Array.isArray(productStatusChange?.approvalRequests)
      ? [...productStatusChange.approvalRequests]
      : [];

    let lastApproval = null;
    if (approvalRequests.length > 0) {
      const sortedApprovals = approvalRequests.sort((a, b) => {
        const dateA = new Date(a.requestedAt || a.createdAt || '1970-01-01');
        const dateB = new Date(b.requestedAt || b.createdAt || '1970-01-01');
        return dateB.getTime() - dateA.getTime(); // Descending
      });
      lastApproval = sortedApprovals[0];
    }

    // Build PDF content - matching the print report structure
    const content = [
      // Header with logo and document ID
      {
        columns: [
          companyLogoBase64 ? {
            image: logoImageKey,
            width: 120,
            alignment: 'left'
          } : { text: '', width: 120 },
          {
            text: 'EN.05 ΟΔ.01/06-ΓΔ.31',
            alignment: 'right',
            fontSize: 11,
            color: '#666666',
            margin: [0, 0, 0, 10]
          }
        ],
        margin: [0, 0, 0, 20]
      },

      // Top separator line under header (full width)
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 0,
            x2: 515,
            y2: 0,
            lineWidth: 2,
            lineColor: '#000000',
          },
        ],
        margin: [0, -6, 0, 12],
      },
      
      // Main Form Table
      {
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            // Title Row
            [
              {
                text: 'MERCK PRODUCT STATUS CHANGE CONFIRMATION',
                colSpan: 4,
                style: 'titleCell',
                alignment: 'center'
              },
              {}, {}, {}
            ],
            
            // Product Name Row (full width)
            [
              {
                text: 'Name of Product:',
                style: 'labelCell'
              },
              {
                text: productStatusChange?.productName || '',
                colSpan: 3,
                style: 'valueCell'
              },
              {}, {}
            ],
            
            // Item Code and Batch Number Row
            [
              {
                text: 'Item Code:',
                style: 'labelCell'
              },
              {
                text: productStatusChange?.itemCode || '',
                style: 'valueCell'
              },
              {
                text: 'Batch Number:',
                style: 'labelCell'
              },
              {
                text: productStatusChange?.batchNumber || '',
                style: 'valueCell'
              }
            ],
            
            // Expiry Date and Quantity Row
            [
              {
                text: 'Expiry Date:',
                style: 'labelCell'
              },
              {
                text: formatDate(productStatusChange?.expiryDate),
                style: 'valueCell'
              },
              {
                text: 'Quantity (items):',
                style: 'labelCell'
              },
              {
                text: productStatusChange?.quantity?.toString() || '',
                style: 'valueCell'
              }
            ],
            
            // Merck Reference and Certificate Date Row
            [
              {
                text: 'Merck Reference: (ManGo ID)',
                style: 'labelCell'
              },
              {
                text: productStatusChange?.merckReference || '',
                style: 'valueCell'
              },
              {
                text: 'Merck Batch Disposition Certificate Date:',
                style: 'labelCell'
              },
              {
                text: formatDate(productStatusChange?.merckDispositionCertDate),
                style: 'valueCell'
              }
            ],
            
            // Status Row (full width) - boxed and shaded like template
            [
              {
                columns: [
                  {
                    width: 'auto',
                    table: {
                      widths: [16, 'auto'],
                      body: [[
                        {
                          canvas: [
                            {
                              type: 'rect',
                              x: 0,
                              y: 0,
                              w: 12,
                              h: 12,
                              r: 1,
                              lineWidth: 1.2,
                              lineColor: '#000000'
                            },
                            ...(isQuarantined ? [{
                              type: 'polyline',
                              lineWidth: 1.4,
                              closePath: false,
                              color: '#0078D4',
                              points: [
                                { x: 1.8, y: 6.5 },
                                { x: 4.5, y: 9.5 },
                                { x: 10, y: 2.0 }
                              ]
                            }] : [])
                          ],
                          margin: [0, 0, 4, 0]
                        },
                        { text: 'Quarantined', margin: [0, 0, 18, 0] }
                      ]]
                    },
                    layout: 'noBorders'
                  },
                  {
                    width: 'auto',
                    table: {
                      widths: [16, 'auto'],
                      body: [[
                        {
                          canvas: [
                            {
                              type: 'rect',
                              x: 0,
                              y: 0,
                              w: 12,
                              h: 12,
                              r: 1,
                              lineWidth: 1.2,
                              lineColor: '#000000'
                            },
                            ...(isRejected ? [{
                              type: 'polyline',
                              lineWidth: 1.4,
                              closePath: false,
                              color: '#0078D4',
                              points: [
                                { x: 1.8, y: 6.5 },
                                { x: 4.5, y: 9.5 },
                                { x: 10, y: 2.0 }
                              ]
                            }] : [])
                          ],
                          margin: [0, 0, 4, 0]
                        },
                        { text: 'Rejected', margin: [0, 0, 18, 0] }
                      ]]
                    },
                    layout: 'noBorders'
                  },
                  {
                    width: 'auto',
                    table: {
                      widths: [16, 'auto'],
                      body: [[
                        {
                          canvas: [
                            {
                              type: 'rect',
                              x: 0,
                              y: 0,
                              w: 12,
                              h: 12,
                              r: 1,
                              lineWidth: 1.2,
                              lineColor: '#000000'
                            },
                            ...(isReleased ? [{
                              type: 'polyline',
                              lineWidth: 1.4,
                              closePath: false,
                              color: '#0078D4',
                              points: [
                                { x: 1.8, y: 6.5 },
                                { x: 4.5, y: 9.5 },
                                { x: 10, y: 2.0 }
                              ]
                            }] : [])
                          ],
                          margin: [0, 0, 4, 0]
                        },
                        { text: 'Released', margin: [0, 0, 0, 0] }
                      ]]
                    },
                    layout: 'noBorders'
                  }
                ],
                colSpan: 4,
                style: 'valueCell',
                margin: [0, 0, 0, 0]
              },
              {}, {}, {}
            ],
            
            // Comments/Remarks row (full width) with inner notes box
            [
              {
                stack: [
                  {
                    text: 'Comments/Remarks:',
                    bold: true,
                    margin: [0, 0, 0, 10]
                  },
                  {
                    table: {
                      widths: ['*'],
                      body: [[
                        {
                          text:
                            (productStatusChange?.remarks || productStatusChange?.comments || '').trim() ||
                            'N/A',
                          margin: [8, 8, 8, 52]
                        }
                      ]]
                    },
                    layout: {
                      hLineWidth: () => 1,
                      vLineWidth: () => 1,
                      hLineColor: () => '#d1d1d1',
                      vLineColor: () => '#d1d1d1',
                    }
                  }
                ],
                colSpan: 4,
                style: 'valueCell',
                margin: [0, 0, 0, 0]
              },
              {}, {}, {}
            ],
            
            // QP Signature Section (full width)
            [
              {
                stack: [
                  {
                    text: 'ALFAOMEGA SA QP',
                    bold: true,
                    fontSize: 12,
                    margin: [0, 0, 0, 10]
                  },
                  {
                    text: 'Name / Signature / Date:',
                    bold: true,
                    margin: [0, 0, 0, 8]
                  },
                  {
                    text: [
                      productStatusChange?.qpUser?.userDisplayName || productStatusChange?.qpUser?.name || '',
                      productStatusChange?.qpUserSignedAt ? ` - ${formatDateTime(productStatusChange.qpUserSignedAt)}` : ''
                    ],
                    margin: [0, 0, 0, 0]
                  },
                  {
                    canvas: [
                      {
                        type: 'line',
                        x1: 0,
                        y1: 8,
                        x2: 455,
                        y2: 8,
                        lineWidth: 1,
                        lineColor: '#000000'
                      }
                    ],
                    margin: [0, 12, 0, 0]
                  }
                ],
                colSpan: 4,
                style: 'valueCell',
                margin: [10, 15, 10, 15]
              },
              {}, {}, {}
            ]
          ]
        },
        layout: {
          hLineWidth: (i, node) => {
            if (i === 0 || i === node.table.body.length) return 2; // Top and bottom borders
            return 1; // Inner borders
          },
          vLineWidth: () => 1,
          hLineColor: () => '#000000',
          vLineColor: () => '#000000',
          paddingLeft: () => 10,
          paddingRight: () => 10,
          paddingTop: () => 10,
          paddingBottom: () => 10,
        },
        margin: [0, 0, 0, 0]
      }
    ];

    // Append Last Approval Status section if available
    if (lastApproval) {
      content.push({
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              {
                text: 'LAST APPROVAL STATUS',
                colSpan: 4,
                style: 'titleCell',
                alignment: 'center',
              },
              {}, {}, {},
            ],
            [
              { text: 'Approval Code:', style: 'labelCell' },
              { text: lastApproval.code || 'N/A', style: 'valueCell' },
              { text: 'Status:', style: 'labelCell' },
              { text: lastApproval.status?.name || lastApproval.status || 'N/A', style: 'valueCell' },
            ],
            [
              { text: 'Title:', style: 'labelCell' },
              {
                text: lastApproval.title || 'N/A',
                style: 'valueCell',
                colSpan: 3,
              },
              {}, {},
            ],
            [
              { text: 'Requested By:', style: 'labelCell' },
              {
                text:
                  lastApproval.requestedBy?.userDisplayName ||
                  lastApproval.requestedBy?.name ||
                  'N/A',
                style: 'valueCell',
              },
              { text: 'Requested Date:', style: 'labelCell' },
              {
                text: formatDateTime(lastApproval.requestedAt),
                style: 'valueCell',
              },
            ],
            [
              { text: 'Due Date:', style: 'labelCell' },
              {
                text: formatDate(lastApproval.dueDate),
                style: 'valueCell',
              },
              { text: 'Completed Date:', style: 'labelCell' },
              {
                text: formatDateTime(lastApproval.completedAt),
                style: 'valueCell',
              },
            ],
          ],
        },
        layout: {
          hLineWidth: (i, node) => {
            if (i === 0 || i === node.table.body.length) return 2;
            return 1;
          },
          vLineWidth: () => 1,
          hLineColor: () => '#000000',
          vLineColor: () => '#000000',
          paddingLeft: () => 10,
          paddingRight: () => 10,
          paddingTop: () => 10,
          paddingBottom: () => 10,
        },
        margin: [0, 20, 0, 0],
      });
    }

    // PDF Document Definition
    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 50],
      defaultStyle: {
        font: 'Roboto',
        fontSize: 11,
        lineHeight: 1.4,
        color: '#000000'
      },
      header: function(currentPage, pageCount) {
        if (companyLogoBase64 && currentPage > 1) {
          return {
            image: logoImageKey,
            width: 120,
            alignment: 'left',
            margin: [40, 20, 0, 0]
          };
        }
        return null;
      },
      footer: function(currentPage, pageCount) {
        return {
          text: `Page ${currentPage} of ${pageCount} | Generated: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
          alignment: 'center',
          fontSize: 8,
          color: '#999999',
          margin: [0, 10, 0, 0]
        };
      },
      content: content,
      images: companyLogoBase64 ? {
        [logoImageKey]: companyLogoBase64
      } : {},
      styles: {
        titleCell: {
          fontSize: 14,
          bold: true,
          alignment: 'center',
          padding: [15, 10, 15, 10]
        },
        labelCell: {
          fontSize: 11,
          bold: true,
          color: '#000000',
          padding: [10, 10, 10, 10]
        },
        valueCell: {
          fontSize: 11,
          color: '#000000',
          padding: [10, 10, 10, 10]
        }
      }
    };

    // Generate and download PDF (pass vfs as 4th arg so fonts work in all bundlers)
    const pdfMakeApi = pdfMake.default || pdfMake;
    pdfMakeApi.createPdf(docDefinition, undefined, undefined, vfs).download(
      `Product-Status-Change-${productStatusChange?.id ?? 'report'}.pdf`
    );
    
    return { success: true };
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
};
